/**
 * LandingPage — event picker. Pick an event, get taken to
 * /landing/:eventId where the split-screen editor lives.
 *
 * LandingDetailPage — the editor shell.
 */

import { useNavigate, useParams } from 'react-router-dom'
import { Calendar, FileImage } from 'lucide-react'
import { PageLayout } from '../components/PageLayout'
import { useAuth } from '../contexts/AuthContext'
import type { EventSummary } from '../contexts/AuthContext'
import { LandingEditor } from '../components/landing/LandingEditor'
import { formatPacificLongDate } from '@/lib/pacific-date'

function fmtDate(iso: string): string {
  return formatPacificLongDate(iso) || iso
}

function deriveEventShortname(ev: EventSummary): string {
  const base = ev.EventName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40)
  return base ? `${base}-${ev.EventID}` : `event-${ev.EventID}`
}

export function LandingPage() {
  const navigate = useNavigate()
  const { events } = useAuth()
  const hasEvents = Array.isArray(events) && events.length > 0

  return (
    <PageLayout
      title="Landing pages"
      subtitle="Edit Butler Amusements e-ticket landing pages. Pick an event to open the editor."
    >
      {!hasEvents && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center dark:border-slate-600 dark:bg-slate-800">
          <FileImage className="mx-auto mb-3 h-8 w-8 text-marquee-500" aria-hidden="true" />
          <p className="text-sm text-slate-500">No events loaded yet.</p>
        </div>
      )}
      {hasEvents && (
        <ul
          data-testid="landing-event-list"
          className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
        >
          {events.map((ev) => (
            <li
              key={ev.EventID}
              className="rounded-2xl border border-slate-200 bg-white p-4 transition-colors hover:border-butler-400 dark:border-slate-700 dark:bg-slate-800"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{ev.EventName}</h3>
                  <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                    <Calendar className="h-3 w-3" aria-hidden="true" />
                    {fmtDate(ev.StartDate)} – {fmtDate(ev.EndDate)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate(`/landing/${ev.EventID}`)}
                  aria-label={`Open editor for ${ev.EventName}`}
                  className="rounded-md bg-butler-500 px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-white hover:bg-butler-600"
                >
                  Open editor
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </PageLayout>
  )
}

export function LandingDetailPage() {
  const navigate = useNavigate()
  const { eventId: raw } = useParams()
  const eventId = Number.parseInt(raw || '', 10)
  const { events } = useAuth()
  const ev = Array.isArray(events) ? events.find((e) => e.EventID === eventId) : null

  if (!Number.isInteger(eventId) || eventId <= 0) {
    return (
      <PageLayout title="Landing page" subtitle="Invalid event id">
        <button
          type="button"
          onClick={() => navigate('/landing')}
          className="text-sm text-butler-600 underline"
        >
          Back to landing list
        </button>
      </PageLayout>
    )
  }

  const shortname = ev ? deriveEventShortname(ev) : `event-${eventId}`

  return (
    <PageLayout
      title={ev ? ev.EventName : `Event ${eventId}`}
      subtitle="Side-by-side landing editor — current vs draft, with AI variations, templates, and Wave L deploy."
      actions={
        <button
          type="button"
          onClick={() => navigate('/landing')}
          className="rounded-md border border-slate-200 px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-50"
        >
          ← All events
        </button>
      }
    >
      <LandingEditor
        eventId={eventId}
        eventShortname={shortname}
        eventStartIso={ev?.StartDate || null}
      />
    </PageLayout>
  )
}
